import { InputCapture } from '@canvas/input';

import { createDeleteIntentHighlightEffect } from './effects/highlights/create-delete-intent-highlight-effect';
import { createHighlightsEffect } from './effects/highlights/create-highlights-effect';
import { HighlightStyleManager } from './effects/highlights/utils/highlight-style-manager';
import { EffectManager } from './effects/manager/effect-manager';
import { type SelectionEffectFactory } from './effects/types';
import { createContextMenuInput } from './inputs/context-menu-input/create-context-menu-input';
import { InputManager } from './inputs/manager/input-manager';
import { createMouseInput } from './inputs/mouse-input/create-mouse-input';
import { type SelectionInputFactory } from './inputs/types';
import { SelectionsMachine } from './machine/selection-machine';

interface InitializeSelectionsMachineParams {
  inputs?: SelectionInputFactory[];
  effects?: SelectionEffectFactory[];
}

/**
 * Initializes the selections machine for all selections state, effects, etc.
 * in Databases.
 */
export function initializeSelectionsMachine(params?: InitializeSelectionsMachineParams) {
  const inputCapture = InputCapture.from(document.body);
  const machine = new SelectionsMachine();
  const inputManager = new InputManager(machine, [
    (m) => createMouseInput(m, inputCapture),
    (m) => createContextMenuInput(m),
    ...(params?.inputs || []),
  ]);
  const effectManager = new EffectManager(machine, [
    () => createHighlightsEffect(new HighlightStyleManager()),
    createDeleteIntentHighlightEffect,
    ...(params?.effects || []),
  ]);

  inputManager.setup();
  effectManager.setup();

  /**
   * This must be called when switching content ids and in cases where proper cleanup
   * is required.
   */
  const destroy = () => {
    effectManager.dispose();
    inputManager.dispose();
    inputCapture.dispose();
  };

  return {
    machine,
    destroy,
  };
}
